import { useState, useRef, useEffect, useCallback } from "react";
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { updateProject } from "../../api/projects";
import { FaFileAlt, FaExpand, FaCompress, FaCircle } from 'react-icons/fa';
import { SowEstimator } from "./SowEstimator";
import { useCanEditProject, useProjectAccessContext } from '../../context/projectAccess';
import { SOW_PARSER_ENABLED } from '../../config/featureFlags';

interface TabScopeOfWorkProps {
  projectId?: string;
  projectData: any;
  onProjectUpdate?: () => void;
  projectUsers?: ProjectUserWithRole[];
}

export const TabScopeOfWork: React.FC<TabScopeOfWorkProps> = ({ projectId, projectData, onProjectUpdate, projectUsers }) => {
  const { keycloak } = useKeycloak();
  const { error: projectAccessError } = useProjectAccessContext();
  const canEdit = useCanEditProject();
  const token = keycloak?.token;

  const currentUserEmail = keycloak?.tokenParsed?.email;
  const currentUserSub = keycloak?.tokenParsed?.sub;
  const currentProjectUser = projectUsers?.find(
    pu => pu.user?.email === currentUserEmail || pu.user_id === currentUserSub
  );
  const isProjectOwner = projectData?.owner_id === currentUserSub || projectData?.owner_id === currentProjectUser?.user_id;
  const allowEdit = canEdit || isProjectOwner;

  const [description, setDescription] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasChanges = description !== (projectData.description || '');

  const adjustTextareaHeight = useCallback(() => {
    if (textareaRef.current) {
      const minHeight = isExpanded ? 500 : 200;
      const maxHeight = isExpanded ? 2000 : 400;

      if (!isExpanded) {
        textareaRef.current.style.height = minHeight + 'px';
        const scrollHeight = textareaRef.current.scrollHeight;
        if (scrollHeight > minHeight) {
          const newHeight = Math.min(scrollHeight, maxHeight);
          textareaRef.current.style.height = newHeight + 'px';
        }
      } else {
        textareaRef.current.style.height = 'auto';
        const scrollHeight = textareaRef.current.scrollHeight;
        const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
        textareaRef.current.style.height = newHeight + 'px';
      }
    }
  }, [isExpanded]);

  useEffect(() => {
    setDescription(projectData.description || '');
  }, [projectData.description]);

  useEffect(() => {
    adjustTextareaHeight();
  }, [description, isExpanded]);

  /**
   * Persist the editor's text on its own.
   *
   * `POST /sow/apply` validates the top-level project fields but leaves them to
   * `PUT /projects/{id}` (FD2-960, workflow steps 11-12), so approving an
   * estimate cannot be relied on to save the scope text it came from — the
   * estimator calls this first when the editor is dirty.
   */
  const persistScope = useCallback(async () => {
    if (!token || !projectId) throw new Error('Authentication required');
    await updateProject(token, projectId, { ...projectData, description });
  }, [token, projectId, projectData, description]);

  const handleSave = async () => {
    if (!token || !projectId) {
      toast.error('Authentication required');
      return;
    }

    setIsSaving(true);
    try {
      await persistScope();
      toast.success('Scope of work updated successfully');
    } catch (error) {
      console.error('Error updating description:', error);
      toast.error('Failed to update scope of work');
      setDescription(projectData.description || '');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    setDescription(projectData.description || '');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      if (hasChanges) handleSave();
    }
  };

  const getDescriptionStatus = () => {
    if (isSaving) return { label: 'Saving...', color: '#F59E0B' };
    if (!hasChanges) return { label: 'Saved', color: '#10B981' };
    return { label: 'Unsaved changes', color: '#EF4444' };
  };

  const status = getDescriptionStatus();
  const wordCount = description?.trim().split(/\s+/).filter((word: string) => word.length > 0).length || 0;
  const charCount = description?.length || 0;

  return (
    <div className="p-6">
      <div className="max-w-[1280px] mx-auto space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FaFileAlt className="text-[#171717] w-5 h-5" />
            <h3 className="text-lg font-semibold">Scope of work</h3>

            {/* Status Indicator */}
            <div className="flex items-center space-x-2">
              <div className="relative">
                {isSaving && (
                  <div className="absolute inset-0 w-5 h-5 -ml-1.5 -mt-1.5 rounded-full border-2 border-transparent animate-spin border-t-blue-500/60 border-r-blue-500/60" style={{ animation: 'spin 3s linear infinite' }} />
                )}
                <FaCircle className="w-2 h-2" style={{ color: status.color }} />
              </div>
              <span className="text-sm text-[#888]">{status.label}</span>
            </div>

            {projectAccessError && (
              <span className="text-sm text-[#b45309]" role="status">
                Couldn't load permissions — editing is unavailable.
              </span>
            )}

            {/* Quick Stats */}
            <div className="flex items-center space-x-4 text-sm text-[#888]">
              <span>{wordCount} words</span>
              <div className="w-px h-4 bg-[#ddd]"></div>
              <span>{charCount} characters</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Save / Discard buttons — only when there are changes */}
            {allowEdit && hasChanges && (
              <>
                <button
                  onClick={handleDiscard}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-[#555] bg-white border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] hover:border-[#ccc] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-default"
                >
                  Discard
                </button>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-[#171717] rounded-lg hover:bg-[#333] transition-all cursor-pointer disabled:opacity-50 disabled:cursor-default flex items-center space-x-2"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30 border-t-white"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save</span>
                  )}
                </button>
              </>
            )}

            {/* Expand/Collapse */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`flex items-center space-x-2 px-4 py-2.5 text-sm font-medium rounded-lg transition-all cursor-pointer ${
                isExpanded
                  ? 'bg-[#f5f5f5] text-[#171717] border border-[#eaeaea]'
                  : 'bg-[#fafafa] text-[#555] hover:bg-[#f5f5f5] border border-[#eaeaea]'
              }`}
            >
              {isExpanded ? <FaCompress className="w-3.5 h-3.5" /> : <FaExpand className="w-3.5 h-3.5" />}
              <span>{isExpanded ? 'Compact' : 'Expand'}</span>
            </button>
          </div>
        </div>

        {/* Editor */}
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={description}
            onChange={(e) => allowEdit && setDescription(e.target.value)}
            onFocus={() => allowEdit && setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onKeyDown={allowEdit ? handleKeyDown : undefined}
            readOnly={!allowEdit}
            placeholder={allowEdit ? "Enter a comprehensive project description including goals, scope, requirements, and timeline..." : "You don't have permission to edit the scope of work."}
            className={`w-full p-4 border rounded-lg resize-none font-mono text-sm leading-relaxed transition-all duration-200 ${
              !allowEdit
                ? 'border-[#eaeaea] bg-[#f5f5f5] text-[#555] cursor-not-allowed'
                : isFocused
                  ? 'border-[#888] bg-[#f5f5f5]/30 ring-2 ring-blue-100'
                  : hasChanges
                    ? 'border-amber-400 bg-amber-50/20 hover:border-amber-500'
                    : 'border-[#eaeaea] bg-[#fafafa]/50 hover:border-[#bbb]'
            }`}
            style={{
              minHeight: isExpanded ? '500px' : '200px',
              fontSize: '13px',
              lineHeight: '1.5'
            }}
          />

          {allowEdit && isFocused && (
            <div className="absolute bottom-3 right-3 text-xs text-[#aaa] bg-white/80 px-2 py-1 rounded">
              {hasChanges ? 'Ctrl+Enter to save' : 'Edit to enable Save'}
            </div>
          )}
          {!allowEdit && (
            <div className="absolute bottom-3 right-3 text-xs text-[#aaa] bg-white/80 px-2 py-1 rounded">
              Read-only — your role does not allow editing
            </div>
          )}
        </div>

        {/* AI SOW parser — reads the editor above, so there is only one input */}
        {SOW_PARSER_ENABLED && projectId && (
          <SowEstimator
            projectId={projectId}
            projectData={projectData}
            sowText={description}
            onPersistScope={persistScope}
            canEdit={allowEdit}
            onApplied={onProjectUpdate}
          />
        )}
      </div>
    </div>
  );
};
